/**
 * Dataset — OneGrid 데이터 엔진
 *
 * - 컬럼형 저장: 숫자·불리언은 Float64Array, 문자열·날짜는 사전 인코딩(Uint32Array 코드 + 사전 배열)
 * - 행 상태: NORMAL / INSERT / UPDATE / DELETE (XPlatform Dataset rowtype 과 같은 의미)
 * - 원본값 보관: 수정된 셀의 최초값을 기억 → 되돌리기, 바뀐 컬럼만 전송, 원래 값으로 되돌리면 NORMAL 복귀
 * - 물리 행 번호는 바뀌지 않는다(삭제는 논리 삭제). 화면 순서는 뷰(행 번호 배열)가 정한다.
 */
export const RowState = { NORMAL: 0, INSERT: 1, UPDATE: 2, DELETE: 4, DISCARDED: 8 };
const collator = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });
export class StrColumn {
    constructor(id, type, cap, into) {
        this.id = id;
        this.type = type;
        this.kind = 'str';
        this.dict = [null];
        this.map = new Map();
        this.rankCache = null;
        this.codes = into ?? new Uint32Array(cap);
    }
    encode(v) {
        if (v == null || v === '')
            return 0;
        const s = typeof v === 'string' ? v : String(v);
        let c = this.map.get(s);
        if (c === undefined) {
            c = this.dict.length;
            this.dict.push(s);
            this.map.set(s, c);
            this.rankCache = null;
        }
        return c;
    }
    get(r) { return this.dict[this.codes[r]]; }
    set(r, v) { this.codes[r] = this.encode(v); }
    codeOf(s) { return this.map.get(s); }
    grow(cap, into) { const n = into ?? new Uint32Array(cap); n.set(this.codes); this.codes = n; }
    /** 사전 항목의 정렬 순위 (한국어 Collator, 사전 크기만큼만 비교) — null 은 맨 뒤 */
    rank() {
        if (this.rankCache)
            return this.rankCache;
        const n = this.dict.length;
        const order = [];
        for (let i = 1; i < n; i++)
            order.push(i);
        order.sort((a, b) => collator.compare(this.dict[a], this.dict[b]));
        const r = new Uint32Array(n);
        r[0] = 0xffffffff;
        for (let i = 0; i < order.length; i++)
            r[order[i]] = i;
        return (this.rankCache = r);
    }
}
export class NumColumn {
    constructor(id, type, cap, into) {
        this.id = id;
        this.type = type;
        this.kind = 'num';
        this.vals = (into ?? new Float64Array(cap)).fill(NaN);
    }
    get(r) {
        const v = this.vals[r];
        if (v !== v)
            return null;
        return this.type === 'boolean' ? v !== 0 : v;
    }
    set(r, v) { this.vals[r] = toNumber(v, this.type); }
    grow(cap, into) { const n = (into ?? new Float64Array(cap)).fill(NaN); n.set(this.vals); this.vals = n; }
}
export function toNumber(v, type = 'number') {
    if (v == null || v === '')
        return NaN;
    if (type === 'boolean') {
        if (typeof v === 'boolean')
            return v ? 1 : 0;
        if (typeof v === 'string')
            return /^(1|y|yes|true|t)$/i.test(v) ? 1 : 0;
        return Number(v) ? 1 : 0;
    }
    if (typeof v === 'number')
        return v;
    const n = Number(String(v).replace(/,/g, ''));
    return n;
}
export function mapServerErrors(errors, rows) {
    return (errors ?? []).map(e => ({ row: rows[e.section]?.[e.index] ?? -1, col: e.field ?? null, message: e.message, section: e.section, index: e.index, ...(e.level && e.level !== 'error' ? { level: e.level } : {}) }));
}
/** 복합 키 값 구분자 (keyOf·findByKey 의 문자열 형식) */
export const KEY_SEP = '\u001f';
/** 키 지정 → 컬럼 목록 (순수). 'a;b' 는 나눔, 빈 것은 null */
export function keyColsOf(k) {
    if (k == null)
        return null;
    const a = (Array.isArray(k) ? k : String(k).split(';')).map(s => String(s).trim()).filter(Boolean);
    return a.length ? a : null;
}
/** 값들 → 키 문자열 (순수). 하나라도 비면 null (키가 아직 없는 새 행) */
export function joinKey(vals) {
    if (vals.length === 1) {
        const v = vals[0];
        return v == null || v === '' ? null : String(v);
    }
    for (const v of vals)
        if (v == null || v === '')
            return null;
    return vals.map(v => String(v)).join(KEY_SEP);
}
/** 키 값(문자열·숫자·배열·객체) → 키 문자열 (순수). 객체면 키 컬럼 값을 뽑음 */
export function keyString(key, cols) {
    if (key == null)
        return null;
    if (Array.isArray(key))
        return joinKey(key);
    if (typeof key === 'object')
        return joinKey(cols.map(c => key[c]));
    return key === '' ? null : String(key);
}
export class Dataset {
    constructor(columns, capacity = 64) {
        /** 행 키 컬럼 (예: 'ordSeq', 복합 키 ['ptno', 'ordSeq'] 또는 'ptno;ordSeq'). applyTransaction·mergeColumnar·그리드 포커스 유지에 쓴다 */
        this.keyCol = null;
        this.keyMap = null;
        this.keyMapVer = -1;
        this.keyMapSig = '';
        this.cols = {};
        this.length = 0;
        /** row → (col → 최초값). 수정된 NORMAL/UPDATE 행만 */
        this.orig = new Map();
        this.listeners = new Set();
        this.batchDepth = 0;
        this.batchDirty = false;
        /** 화면 갱신 판단용 — 데이터가 바뀔 때마다 증가 */
        this.version = 0;
        /** 정렬·필터 캐시 무효화용 — 구조(행 추가/삭제/적재)가 바뀔 때 증가 */
        this.structureVersion = 0;
        // ───────────── 셀 메모 ─────────────
        /** 셀 메모: '행 키\u0001컬럼' → 메모. 행 키는 keyCol 값(없으면 '#물리행' — 저장 전 행은 서버에 남지 않음) */
        this.notes = new Map();
        /** 마지막 getChanges() 가 보낸 행 순서 (서버 오류 index → 물리 행 매핑용) */
        this.lastChangeRows = { inserted: [], updated: [], deleted: [] };
        this.columns = columns.map(c => (typeof c === 'string' ? { id: c, type: 'string' } : { type: 'string', ...c }));
        this.cap = Math.max(16, capacity);
        this.makeColumns(this.cap);
        this.state = new Uint8Array(this.cap);
    }
    hasColumn(id) { return id in this.cols; }
    addColumn(c) {
        if (this.cols[c.id])
            return;
        const col = { type: 'string', ...c };
        this.columns.push(col);
        this.cols[c.id] = makeColumn(col, this.cap);
    }
    // ───────────── 이벤트 ─────────────
    on(fn) { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
    emit(e) {
        this.version++;
        if (e.type !== 'cell')
            this.structureVersion++;
        if (this.batchDepth) {
            this.batchDirty = true;
            return;
        }
        for (const fn of this.listeners)
            fn(e);
    }
    /** 여러 변경을 묶어 이벤트를 한 번만 보낸다 */
    batch(fn) {
        this.batchDepth++;
        try {
            return fn();
        }
        finally {
            if (--this.batchDepth === 0 && this.batchDirty) {
                this.batchDirty = false;
                this.structureVersion++;
                for (const l of this.listeners)
                    l({ type: 'batch' });
            }
        }
    }
    // ───────────── 적재 ─────────────
    ensure(n) {
        if (n <= this.cap)
            return;
        let cap = this.cap;
        while (cap < n)
            cap *= 2;
        const cols = Object.values(this.cols), views = slabViews(cols.map(c => c.kind === 'num'), cap);
        cols.forEach((c, i) => c.grow(cap, views[i]));
        const s = new Uint8Array(cap);
        s.set(this.state);
        this.state = s;
        this.cap = cap;
    }
    clear() {
        for (const c of this.columns)
            this.cols[c.id] = makeColumn(c, 16);
        this.cap = 16;
        this.state = new Uint8Array(16);
        this.length = 0;
        this.orig.clear();
        this.emit({ type: 'load' });
    }
    /** 행 객체 배열 바인딩 (일반 JSON). append=false 면 기존 데이터를 지운다 */
    loadObjects(rows, append = false) {
        if (!append)
            this.resetStorage(rows.length);
        const base = this.length, n = rows.length;
        this.ensure(base + n);
        for (const def of this.columns) {
            const col = this.cols[def.id], key = def.id;
            if (col.kind === 'num') {
                const v = col.vals;
                for (let i = 0; i < n; i++)
                    v[base + i] = toNumber(rows[i][key], col.type);
            }
            else {
                const c = col.codes;
                for (let i = 0; i < n; i++)
                    c[base + i] = col.encode(rows[i][key]);
            }
        }
        this.length += n;
        this.emit({ type: 'load' });
    }
    /** 컬럼형 바인딩 (Spring GridData). 숫자 배열은 그대로 복사 → 가장 빠른 경로 */
    loadColumnar(d, append = false) {
        if (d.columns)
            for (const c of d.columns)
                if (!this.cols[c.id])
                    this.addColumn(c);
        if (!append)
            this.resetStorage(d.n);
        const base = this.length, n = d.n;
        this.ensure(base + n);
        for (const def of this.columns) {
            const col = this.cols[def.id], src = d.data[def.id];
            if (!src)
                continue;
            if (col.kind === 'num') {
                const v = col.vals;
                if (col.type !== 'boolean' && (src instanceof Float64Array || src instanceof Int32Array || (Array.isArray(src) && typeof src[0] === 'number'))) {
                    for (let i = 0; i < n; i++) {
                        const x = src[i];
                        v[base + i] = x == null ? NaN : x;
                    }
                }
                else
                    for (let i = 0; i < n; i++)
                        v[base + i] = toNumber(src[i], col.type);
            }
            else {
                const c = col.codes;
                for (let i = 0; i < n; i++)
                    c[base + i] = col.encode(src[i]);
            }
        }
        this.length += n;
        this.emit({ type: 'load' });
    }
    /**
     * 빈 행 n 개로 다시 잡기 (서버 행 모델: 전체 건수만큼 자리만 만들고 값은 블록 단위로 채운다)
     */
    allocate(n) { this.resetStorage(n); this.length = n; this.emit({ type: 'load' }); }
    /** 끝에 컬럼형 행을 붙인다 (행 상태 NORMAL, 이벤트 없음 — 서버 그룹 멤버 지연 적재). 붙인 첫 물리 행 번호 */
    appendColumnarSilent(d) {
        const base = this.length;
        this.ensure(base + d.n);
        this.length = base + d.n;
        this.fillColumnar(base, d);
        return base;
    }
    /** offset 부터 컬럼형 값을 덮어쓴다 (행 상태·이벤트 없음 — 서버 행 모델 블록 채우기) */
    fillColumnar(offset, d) {
        const n = Math.min(d.n, this.length - offset);
        for (const def of this.columns) {
            const col = this.cols[def.id], src = d.data[def.id];
            if (!src)
                continue;
            if (col.kind === 'num') {
                const v = col.vals;
                for (let i = 0; i < n; i++)
                    v[offset + i] = toNumber(src[i], col.type);
            }
            else {
                const c = col.codes;
                for (let i = 0; i < n; i++)
                    c[offset + i] = col.encode(src[i]);
            }
        }
        this.version++;
    }
    /** 모든 컬럼 저장소를 몇 개의 큰 버퍼로 한꺼번에 잡는다 */
    makeColumns(cap) {
        const views = slabViews(this.columns.map(c => c.type === 'number' || c.type === 'boolean'), cap);
        this.columns.forEach((c, i) => { this.cols[c.id] = makeColumn(c, cap, views[i]); });
    }
    resetStorage(n) {
        this.cap = Math.max(16, n);
        this.makeColumns(this.cap);
        this.state = new Uint8Array(this.cap);
        this.length = 0;
        this.orig.clear();
    }
    // ───────────── 값 ─────────────
    get(row, col) {
        const c = this.cols[col];
        return c ? c.get(row) : null;
    }
    /** 셀 값 변경. 행 상태와 원본값을 자동 관리한다. 반환: 실제로 바뀌었는지 */
    set(row, col, value) {
        const c = this.cols[col];
        if (!c || row < 0 || row >= this.length)
            return false;
        const old = c.get(row);
        const norm = normalize(value, c);
        if (sameValue(old, norm))
            return false;
        const st = this.state[row];
        // 삭제 대기(D) 행도 원본값을 기록 (14차) — 지운 뒤 코드로 고쳐도 getChanges 의 deleted 는 원본값, revertRow 는 원래 값으로
        if (st === RowState.NORMAL || st === RowState.UPDATE || st === RowState.DELETE) {
            let m = this.orig.get(row);
            if (!m) {
                m = new Map();
                this.orig.set(row, m);
            }
            if (!m.has(col))
                m.set(col, old);
            else if (sameValue(m.get(col), norm))
                m.delete(col); // 원래 값으로 돌아옴
            c.set(row, norm);
            if (!m.size)
                this.orig.delete(row);
            if (st !== RowState.DELETE) {
                const newSt = m.size ? RowState.UPDATE : RowState.NORMAL;
                if (newSt !== st)
                    this.state[row] = newSt;
            }
        }
        else
            c.set(row, norm);
        this.emit({ type: 'cell', row, col, value: norm, old });
        return true;
    }
    /** 원본값 (수정 전). 수정되지 않았으면 현재값 */
    getOrig(row, col) {
        const m = this.orig.get(row);
        return m && m.has(col) ? m.get(col) : this.get(row, col);
    }
    isCellChanged(row, col) { return !!this.orig.get(row)?.has(col); }
    getRow(row) {
        const o = {};
        for (const c of this.columns)
            o[c.id] = this.cols[c.id].get(row);
        return o;
    }
    rowState(row) { return this.state[row]; }
    isVisibleRow(row) { const s = this.state[row]; return s !== RowState.DELETE && s !== RowState.DISCARDED; }
    // ───────────── 행 추가·삭제·되돌리기 ─────────────
    addRow(values = {}) {
        this.ensure(this.length + 1);
        const r = this.length++;
        for (const def of this.columns)
            this.cols[def.id].set(r, normalize(values[def.id] ?? null, this.cols[def.id]));
        this.state[r] = RowState.INSERT;
        this.emit({ type: 'rowadd', row: r });
        return r;
    }
    deleteRow(row) {
        const st = this.state[row];
        if (st === RowState.DELETE || st === RowState.DISCARDED)
            return;
        this.state[row] = st === RowState.INSERT ? RowState.DISCARDED : RowState.DELETE;
        this.emit({ type: 'rowdelete', row });
    }
    noteRowKey(row) { const k = this.keyCol ? this.keyOf(row) : null; return k != null && k !== '' ? String(k) : '#' + row; }
    getNote(row, col) { return this.notes.size ? this.notes.get(this.noteRowKey(row) + '\u0001' + col) : undefined; }
    /** 메모 쓰기 (text 가 비면 삭제). 바뀐 메모를 돌려준다 */
    setNote(row, col, text, userId) {
        const rowKey = this.noteRowKey(row), k = rowKey + '\u0001' + col;
        const t = text?.trim() ? text : null;
        const n = t ? { rowKey, col, note: t, userId, at: new Date().toISOString().slice(0, 19) } : null;
        if (n)
            this.notes.set(k, n);
        else if (!this.notes.delete(k))
            return null;
        this.emit({ type: 'state', row });
        return n ?? { rowKey, col, note: '' };
    }
    /** 서버에서 읽은 메모 목록으로 바꾼다 */
    loadNotes(list) {
        this.notes.clear();
        for (const n of list)
            if (n && n.rowKey != null && n.col && n.note)
                this.notes.set(String(n.rowKey) + '\u0001' + n.col, { ...n, rowKey: String(n.rowKey) });
        this.emit({ type: 'state', row: -1 });
    }
    /** 메모가 있는 셀 (보이는 행만): [{ row, col, note }] */
    noteCells() {
        if (!this.notes.size)
            return [];
        const out = [];
        for (let r = 0; r < this.length; r++) {
            if (!this.isVisibleRow(r))
                continue;
            const rk = this.noteRowKey(r) + '\u0001';
            for (const [k, n] of this.notes)
                if (k.startsWith(rk))
                    out.push({ row: r, col: n.col, note: n });
        }
        return out;
    }
    /** 실행 취소용: 행 상태를 직접 바꾼다 (추가 취소 = DISCARDED, 삭제 취소 = 이전 상태). 보이는지 여부가 바뀌면 rowadd/rowdelete */
    restoreRowState(row, st) {
        const cur = this.state[row];
        if (cur === st || row < 0 || row >= this.length)
            return;
        const vis = (s) => s !== RowState.DELETE && s !== RowState.DISCARDED;
        this.state[row] = st;
        this.emit({ type: vis(st) && !vis(cur) ? 'rowadd' : !vis(st) && vis(cur) ? 'rowdelete' : 'state', row });
    }
    /** 행을 추가(I) 상태로 — 값·키는 그대로, 원본값은 버림. 저장 충돌 "다시 추가"(서버에서 지워진 행을 같은 키로 INSERT)에 씀 */
    markInserted(row) {
        const st = this.state[row];
        if (row < 0 || row >= this.length || st === RowState.INSERT || st === RowState.DISCARDED)
            return;
        this.orig.delete(row);
        this.state[row] = RowState.INSERT;
        this.emit({ type: st === RowState.DELETE ? 'rowadd' : 'state', row });
    }
    /** 삭제(D)만 취소: 고친 값은 그대로 두고 UPDATE(고친 값이 있으면) 또는 NORMAL 로. 삭제 행이 아니면 false */
    undeleteRow(row) {
        if (row < 0 || row >= this.length || this.state[row] !== RowState.DELETE)
            return false;
        this.state[row] = this.orig.has(row) ? RowState.UPDATE : RowState.NORMAL;
        this.emit({ type: 'rowadd', row });
        return true;
    }
    /** 행을 조회 직후 상태로 되돌린다 (수정 취소, 삭제 취소, 추가 행은 버림) */
    revertRow(row) {
        const st = this.state[row];
        if (st === RowState.DISCARDED || row < 0 || row >= this.length)
            return; // 버린 행은 그대로 (예전에는 NORMAL 로 되살아남, 14차 수정)
        if (st === RowState.INSERT) {
            this.state[row] = RowState.DISCARDED;
            this.emit({ type: 'rowdelete', row });
            return;
        }
        const m = this.orig.get(row);
        if (m) {
            for (const [col, v] of m)
                this.cols[col].set(row, v);
            this.orig.delete(row);
        }
        if (st !== RowState.NORMAL) {
            this.state[row] = RowState.NORMAL;
            this.emit({ type: st === RowState.DELETE ? 'rowadd' : 'state', row });
        } // 삭제 취소는 다시 보이므로 rowadd (15차)
    }
    /** 모든 변경 취소 */
    revertAll() { this.batch(() => { for (let r = 0; r < this.length; r++)
        if (this.state[r] !== RowState.NORMAL && this.state[r] !== RowState.DISCARDED)
            this.revertRow(r); }); }
    hasChanges() {
        for (let r = 0; r < this.length; r++) {
            const s = this.state[r];
            if (s === RowState.INSERT || s === RowState.UPDATE || s === RowState.DELETE)
                return true;
        }
        return false;
    }
    changeCounts() {
        let i = 0, u = 0, d = 0;
        for (let r = 0; r < this.length; r++) {
            const s = this.state[r];
            if (s === RowState.INSERT)
                i++;
            else if (s === RowState.UPDATE)
                u++;
            else if (s === RowState.DELETE)
                d++;
        }
        return { inserted: i, updated: u, deleted: d };
    }
    /** 서버로 보낼 변경분. updated 에는 바뀐 컬럼의 원본값(_orig)이 붙는다. deleted 는 원본값 기준 */
    getChanges() {
        const out = { inserted: [], updated: [], deleted: [] };
        const rows = { inserted: [], updated: [], deleted: [] };
        this.lastChangeRows = rows;
        for (let r = 0; r < this.length; r++) {
            const s = this.state[r];
            if (s === RowState.INSERT) {
                out.inserted.push(this.getRow(r));
                rows.inserted.push(r);
            }
            else if (s === RowState.UPDATE) {
                out.updated.push({ ...this.getRow(r), _orig: Object.fromEntries(this.orig.get(r) ?? []) });
                rows.updated.push(r);
            }
            else if (s === RowState.DELETE) {
                rows.deleted.push(r);
                const row = this.getRow(r);
                for (const [k, v] of this.orig.get(r) ?? [])
                    row[k] = v;
                out.deleted.push(row);
            }
        }
        return out;
    }
    /** 저장 성공 후 호출: 현재 값을 새 원본으로 확정 */
    commit() {
        for (let r = 0; r < this.length; r++) {
            const s = this.state[r];
            if (s === RowState.DELETE)
                this.state[r] = RowState.DISCARDED;
            else if (s !== RowState.DISCARDED)
                this.state[r] = RowState.NORMAL;
        }
        this.orig.clear();
        this.emit({ type: 'commit' });
    }
    /** 상태·원본값을 건드리지 않고 저장소 값만 바꿈 (낙관적 잠금 버전 컬럼 갱신 등) */
    setBase(row, col, value) {
        const c = this.cols[col];
        if (!c || row < 0 || row >= this.length)
            return;
        c.set(row, normalize(value, c));
        const m = this.orig.get(row);
        if (m?.has(col))
            m.set(col, normalize(value, c));
        this.emit({ type: 'state', row });
    }
    // ───────────── 키 기준 부분 갱신 ─────────────
    /** 키 색인 (키 문자열 → 행). 컬럼 하나면 값 그대로(빠른 경로), 복합 키면 KEY_SEP 로 이은 문자열 */
    keyIndex(key) {
        const cols = keyColsOf(key) ?? [], sig = cols.join(';');
        if (this.keyMap && this.keyMapVer === this.version && this.keyMapSig === sig)
            return this.keyMap;
        const m = new Map();
        if (cols.length === 1) {
            const c = this.cols[cols[0]];
            if (c)
                for (let r = 0; r < this.length; r++) {
                    if (this.state[r] === RowState.DISCARDED)
                        continue;
                    const v = c.get(r);
                    if (v != null)
                        m.set(String(v), r);
                }
        }
        else if (cols.length && cols.every(id => this.cols[id])) {
            const cs = cols.map(id => this.cols[id]);
            for (let r = 0; r < this.length; r++) {
                if (this.state[r] === RowState.DISCARDED)
                    continue;
                const k = joinKey(cs.map(c => c.get(r)));
                if (k != null)
                    m.set(k, r);
            }
        }
        this.keyMap = m;
        this.keyMapVer = this.version;
        this.keyMapSig = sig;
        return m;
    }
    /**
     * 키 값들 → 키 문자열. 키 컬럼 형식으로 정규화한 뒤 이음 (숫자 키 '05'·'1,000' = 저장된 5·1000, 빈 글자 = 키 없음).
     * 색인(keyIndex)은 저장된(정규화된) 값으로 만들므로 찾을 때도 같은 규칙이어야 함 (14차 수정 — 예전에는 String 그대로라 못 찾음)
     */
    keyStr(vals, cols) {
        if (cols.length === 1)
            return this.keyPart(vals[0], cols[0]);
        return joinKey(cols.map((c, j) => { const col = this.cols[c], v = vals[j]; return col ? normalize(v, col) : v; }));
    }
    /** 컬럼 하나 키 (빠른 경로) */
    keyPart(v, c) {
        const col = this.cols[c], n = col ? normalize(v, col) : v;
        return n == null || n === '' ? null : String(n);
    }
    /** 키 지정(값·배열·객체·KEY_SEP 로 이은 문자열) → 키 문자열 */
    keyStrOf(key, cols) {
        if (key == null)
            return null;
        if (Array.isArray(key))
            return this.keyStr(key, cols);
        if (typeof key === 'object')
            return this.keyStr(cols.map(c => key[c]), cols);
        if (cols.length > 1) {
            const parts = String(key).split(KEY_SEP);
            return parts.length === cols.length ? this.keyStr(parts, cols) : keyString(key, cols);
        }
        return this.keyStr([key], cols);
    }
    /** 키 컬럼 목록 (keyCol 이 'a;b'·배열이면 여러 개) */
    keyCols(keyCol = this.keyCol) { return keyColsOf(keyCol); }
    /** 키 값으로 물리 행 찾기 (없으면 -1). 삭제 대기(D) 행도 찾는다. 복합 키는 배열·객체·keyOf 문자열 모두 받음 */
    findByKey(key, keyCol = this.keyCol) {
        const cols = keyColsOf(keyCol);
        if (!cols || key == null)
            return -1;
        const k = this.keyStrOf(key, cols);
        return k == null ? -1 : this.keyIndex(keyCol).get(k) ?? -1;
    }
    /** 행의 키 값: 컬럼 하나면 그 값, 복합 키면 KEY_SEP 로 이은 문자열(하나라도 비면 null), keyCol 이 없으면 행 번호 */
    keyOf(row, keyCol = this.keyCol) {
        const cols = keyColsOf(keyCol);
        if (!cols)
            return row;
        if (cols.length === 1)
            return this.get(row, cols[0]);
        return joinKey(cols.map(c => this.get(row, c)));
    }
    /** 행 객체의 키 문자열 (복합 키 포함) */
    keyOfObject(o, keyCol = this.keyCol) { const cols = keyColsOf(keyCol); return cols ? this.keyStrOf(o, cols) : null; }
    /** 서버 값으로 행 하나 덮어쓰기 (상태는 NORMAL, 원본값 정리) */
    overwriteRow(r, vals, has) {
        let changed = false;
        for (const def of this.columns) {
            if (!has(def.id))
                continue;
            const c = this.cols[def.id], nv = normalize(vals(def.id) ?? null, c);
            if (!sameValue(c.get(r), nv)) {
                c.set(r, nv);
                changed = true;
            }
        }
        if (this.orig.delete(r))
            changed = true;
        if (this.state[r] !== RowState.NORMAL) {
            this.state[r] = RowState.NORMAL;
            changed = true;
        }
        return changed;
    }
    appendRow(vals) {
        this.ensure(this.length + 1);
        const r = this.length++;
        for (const def of this.columns)
            this.cols[def.id].set(r, normalize(vals(def.id) ?? null, this.cols[def.id]));
        this.state[r] = RowState.NORMAL;
        return r;
    }
    /**
     * 키 기준 부분 갱신 — 전체 재조회 없이 행을 추가·수정·제거한다 (AG Grid applyTransaction).
     * update/remove 는 keyCol 로 행을 찾는다. 반영된 행은 서버 값으로 보고 NORMAL 상태가 된다.
     * 물리 행 번호가 유지되므로 그리드의 포커스·체크·스크롤·정렬·필터가 그대로 남는다.
     */
    applyTransaction(tx, keyCol = this.keyCol) {
        const res = { added: [], updated: [], removed: [] };
        const kcs = keyColsOf(keyCol);
        if ((tx.update?.length || tx.remove?.length) && !kcs)
            throw new Error('applyTransaction: keyCol 이 필요합니다');
        for (const o of tx.remove ?? []) {
            // 키 값·배열(복합)·행 객체 모두 받음
            const r = this.findByKey(o, keyCol);
            if (r < 0)
                continue;
            this.state[r] = RowState.DISCARDED;
            this.orig.delete(r);
            res.removed.push(r);
            this.keyMap = null; // 키 색인 무효화
        }
        for (const o of tx.update ?? []) {
            const r = this.findByKey(o, keyCol);
            if (r < 0)
                continue;
            if (this.overwriteRow(r, c => o[c], c => c in o))
                res.updated.push(r);
        }
        for (const o of tx.add ?? [])
            res.added.push(this.appendRow(c => o[c]));
        this.keyMap = null;
        if (res.added.length || res.updated.length || res.removed.length)
            this.emit({ type: 'merge', updated: res.updated });
        return res;
    }
    /**
     * 컬럼형 결과(Spring GridData)를 키로 병합한다. 같은 키는 값 갱신, 새 키는 추가,
     * removeMissing 이면 결과에 없는 행(키 없는 새 행 포함)을 제거한다.
     */
    mergeColumnar(d, opts = {}) {
        const key = opts.key ?? this.keyCol, kcs = keyColsOf(key);
        if (!key || !kcs)
            throw new Error('mergeColumnar: key 가 필요합니다');
        if (d.columns)
            for (const c of d.columns)
                if (!this.cols[c.id])
                    this.addColumn(c);
        const res = { added: [], updated: [], removed: [] };
        const src = d.data, kss = kcs.map(c => src[c]);
        const miss = kcs.find((_, j) => !kss[j]);
        if (miss)
            throw new Error('mergeColumnar: 결과에 키 컬럼 ' + miss + ' 이 없습니다');
        const idx = new Map(this.keyIndex(key)), seen = new Set();
        const has = (c) => !!src[c];
        for (let i = 0; i < d.n; i++) {
            const k = kss.length === 1 ? this.keyPart(kss[0][i], kcs[0]) : this.keyStr(kss.map(a => a[i]), kcs), vals = (c) => src[c]?.[i];
            const r = k == null ? -1 : idx.get(k) ?? -1;
            if (r >= 0) {
                seen.add(r);
                if (this.overwriteRow(r, vals, has))
                    res.updated.push(r);
            }
            else {
                const nr = this.appendRow(vals);
                seen.add(nr);
                res.added.push(nr);
                if (k != null)
                    idx.set(k, nr);
            }
        }
        if (opts.removeMissing)
            for (let r = 0; r < this.length; r++) {
                if (seen.has(r) || this.state[r] === RowState.DISCARDED)
                    continue;
                this.state[r] = RowState.DISCARDED;
                this.orig.delete(r);
                res.removed.push(r);
            }
        this.keyMap = null;
        if (res.added.length || res.updated.length || res.removed.length)
            this.emit({ type: 'merge', updated: res.updated });
        return res;
    }
    /** 삭제·버린 행을 제외한 물리 행 번호 배열 (뷰의 출발점). includeDeleted 면 삭제(D) 행도 (버린 행만 뺌 — 삭제 행 표시 모드) */
    allRows(includeDeleted = false) {
        const out = new Uint32Array(this.length);
        let k = 0;
        if (includeDeleted) {
            for (let r = 0; r < this.length; r++)
                if (this.state[r] !== RowState.DISCARDED)
                    out[k++] = r;
        }
        else
            for (let r = 0; r < this.length; r++)
                if (this.isVisibleRow(r))
                    out[k++] = r;
        return k === this.length ? out : out.slice(0, k);
    }
    toObjects(rows) {
        const idx = rows ?? this.allRows();
        const out = new Array(idx.length);
        for (let i = 0; i < idx.length; i++)
            out[i] = this.getRow(idx[i]);
        return out;
    }
}
function makeColumn(c, cap, into) {
    const t = c.type ?? 'string';
    return t === 'number' || t === 'boolean' ? new NumColumn(c.id, t, cap, into) : new StrColumn(c.id, t, cap, into);
}
/**
 * 컬럼별 저장 배열을 큰 버퍼(최대 256MB) 몇 개에서 잘라 준다.
 * 컬럼마다 따로 할당하면 힙이 클 때 할당마다 전체 GC 가 돌아 넓은 표 적재가 몇 배 느려진다
 * (200컬럼 × 30만 행: 컬럼별 할당 약 1초 → 버퍼 하나 25ms).
 */
const SLAB_BYTES = 256 * 1024 * 1024;
function slabViews(isNum, cap) {
    const out = new Array(isNum.length);
    // 8바이트 정렬을 위해 숫자 컬럼을 먼저 배치
    const order = isNum.map((n, i) => [n, i]).sort((a, b) => Number(b[0]) - Number(a[0]));
    let k = 0;
    while (k < order.length) {
        let bytes = 0, end = k;
        while (end < order.length) {
            const b = cap * (order[end][0] ? 8 : 4);
            if (end > k && bytes + b > SLAB_BYTES)
                break;
            bytes += b;
            end++;
        }
        const buf = new ArrayBuffer(bytes);
        let off = 0;
        for (; k < end; k++) {
            const [num, i] = order[k];
            out[i] = num ? new Float64Array(buf, off, cap) : new Uint32Array(buf, off, cap);
            off += cap * (num ? 8 : 4);
        }
    }
    return out;
}
function normalize(v, c) {
    if (c.kind === 'num') {
        const n = toNumber(v, c.type);
        return n !== n ? null : c.type === 'boolean' ? n !== 0 : n;
    }
    return v == null || v === '' ? null : String(v);
}
function sameValue(a, b) { return a === b || (a == null && b == null); }
